import React from 'react';
import {DefaultButton, PrimaryButton, Stack} from '@fluentui/react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {RootState} from '../../../../store/rootReducer';
import {
    useGetGlobalStorefrontSettingsQuery,
    useGetStorefrontQuery,
    useLazyGetStorefrontLoginLinkQuery,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';

interface IStorefrontPreviewButtonProps {
    storefrontId?: string;
    text?: string;
    primaryDisabled?: boolean;
    /** Draw it as the brand-green primary action instead of the neutral default button. */
    primary?: boolean;
    /** Drop the leading icon, for places where the word on its own is the whole affordance. */
    hideIcon?: boolean;
    /**
     * Also offer a "Sign-in page" link. The main button opens the store authenticated, which never
     * passes through the sign-in page, so styling staged on it (loginPageConfig) is otherwise
     * invisible until publish. This opens the same resolved sub-url's /login route signed out.
     */
    showSignInPage?: boolean;
}

/**
 * Opens the storefront the session is working on, in a new tab.
 *
 * It resolves the preview sub-url when the organization runs draft/live previews and the live one
 * otherwise, the same rule the storefront title bar uses, and goes through the admin login link so
 * the tab lands authenticated rather than on a login form.
 */
const StorefrontPreviewButton: React.FC<IStorefrontPreviewButtonProps> = (props) => {
    const globalSettings = useGetGlobalStorefrontSettingsQuery();
    const storefront = useGetStorefrontQuery(props.storefrontId ?? '', {skip: !props.storefrontId});
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [getLink] = useLazyGetStorefrontLoginLinkQuery();
    const [opening, setOpening] = React.useState(false);

    const resolveSubUrl = () => {
        const data = storefront.data;
        if (!data?.subUrl) return undefined;
        const usePreview = globalSettings.data?.usePreview === true;
        return usePreview
            ? data.previewSubUrl ?? `${data.subUrl}-preview`
            : data.liveSubUrl ?? data.subUrl;
    };

    const open = (signInPage: boolean) => {
        const subUrl = resolveSubUrl();
        if (!subUrl) return;
        setOpening(true);
        getLink(subUrl).unwrap().then((resp) => {
            if (!resp.redirectUrl) {
                toast.error('No storefront link was returned');
                return;
            }
            if (!signInPage) {
                window.open(resp.redirectUrl, '_blank');
                return;
            }
            // The sign-in page must be reached signed OUT, so only the ORIGIN of the authenticated
            // link is kept (it is where this deployment serves the store); the session token in the
            // rest of the URL is deliberately dropped. Same derivation the capture tool uses.
            const origin = new URL(resp.redirectUrl).origin;
            window.open(`${origin}/${encodeURIComponent(organization ?? '')}/${encodeURIComponent(subUrl)}/login`, '_blank');
        }).catch(() => {
            toast.error('Failed to open the storefront');
        }).finally(() => setOpening(false));
    };

    if (!props.storefrontId) return null;

    // One behaviour, two skins. The click path, the disabled rule and the link resolution are the same
    // whichever button is drawn; only the emphasis changes with where it is being shown.
    const Button = props.primary ? PrimaryButton : DefaultButton;
    const disabled = opening || props.primaryDisabled || !storefront.data?.subUrl;

    const mainButton = (
        <Button
            text={props.text ?? 'Open storefront'}
            iconProps={props.hideIcon ? undefined : {iconName: 'OpenInNewWindow'}}
            disabled={disabled}
            onClick={() => open(false)}
        />
    );

    if (!props.showSignInPage) return mainButton;

    return (
        <Stack horizontal tokens={{childrenGap: '0.5em'}}>
            {mainButton}
            <DefaultButton
                text='Sign-in page'
                iconProps={{iconName: 'Signin'}}
                title='Open the sign-in page as a signed-out shopper sees it'
                disabled={disabled}
                onClick={() => open(true)}
            />
        </Stack>
    );
};

export default StorefrontPreviewButton;
